import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { X, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';
import config from '../../config';
import { trackButtonClick } from '../../hooks/useTelemetry';
import { useCurrentPortalPaused } from '../../lib/maintenance';

const SESSION_KEY = 'bmj_exit_intent_shown';
// Internal surfaces (admin + staff portals) must never show the lead-gen popup
const INTERNAL_PREFIXES = ['/admin', '/nurse', '/doctor', '/ops', '/hospital-dashboard', '/admin-login'];
export const isInternalPath = (p = '') => INTERNAL_PREFIXES.some((x) => p === x || p.startsWith(`${x}/`) || p.startsWith(`${x}-`));

export default function ExitIntentModal() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const hidden = isInternalPath(location.pathname);
  const paused = useCurrentPortalPaused();

  useEffect(() => {
    if (hidden || paused) setOpen(false);
  }, [hidden, paused]);

  useEffect(() => {
    if (hidden || paused) return;
    // Once per session
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;

    const isTouch = (window.matchMedia?.('(pointer: coarse)').matches) || ('ontouchstart' in window);

    const trigger = () => {
      // Checked at fire-time so SPA navigation into /admin, /nurse, /doctor,
      // /ops or /hospital-dashboard is also covered.
      if (isInternalPath(window.location.pathname)) return;
      if (sessionStorage.getItem(SESSION_KEY) === '1') return;
      sessionStorage.setItem(SESSION_KEY, '1');
      setOpen(true);
      trackButtonClick('Exit Intent Shown', { surface: isTouch ? 'visibility' : 'mouseout' });
    };

    const onMouseOut = (e) => {
      // Real exit: cursor leaves to the top of the viewport and goes outside document
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        trigger();
      }
    };
    const onVisibility = () => {
      // Mobile fallback: user switches tab / backgrounds app within first 20s
      if (document.visibilityState === 'hidden') trigger();
    };

    // Give the visitor a moment before arming (avoid triggering on fast scrollers)
    const armTimer = setTimeout(() => {
      if (isTouch) {
        document.addEventListener('visibilitychange', onVisibility);
      } else {
        document.addEventListener('mouseout', onMouseOut);
      }
    }, 8000);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hidden]);

  // Lock scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close('escape'); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (reason = 'close') => {
    trackButtonClick('Exit Intent Dismissed', { reason });
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    trackButtonClick('Exit Intent Callback', { source: 'exit_intent' });
    try {
      await axios.post(`${config.API}/callback-request`, {
        name: name || 'Exit-intent visitor',
        phone,
        vaccine_interest: 'From exit-intent popup',
        company_website: honeypot,
      });
      setSubmitted(true);
    } catch { /* swallow */ }
    finally { setLoading(false); }
  };

  if (hidden || !open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="exit-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={() => close('backdrop')}
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="exit-intent-backdrop"
      >
        <motion.div
          key="exit-modal"
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md rounded-2xl bg-gradient-to-br from-[#311b52] to-[#3e195e] text-white shadow-2xl overflow-hidden"
          data-testid="exit-intent-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Wait — request a callback before you go"
        >
          {/* Gold accent line */}
          <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#C5A059] to-transparent" />

          <button
            onClick={() => close('x-button')}
            data-testid="exit-intent-close"
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-white/80" />
          </button>

          <div className="p-7 sm:p-8">
            {submitted ? (
              <div className="text-center py-4" data-testid="exit-intent-success">
                <CheckCircle className="w-12 h-12 text-[#22c55e] mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Poppins' }}>
                  Thank you.
                </h3>
                <p className="text-sm text-white/70" style={{ fontFamily: 'Montserrat' }}>
                  A concierge will reach out within 15 minutes during business hours.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#f37721]/20 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-[#f37721]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#C5A059] mb-1" style={{ fontFamily: 'Montserrat' }}>
                      Before you go
                    </p>
                    <h3 className="text-xl font-bold leading-tight" style={{ fontFamily: 'Poppins' }}>
                      Questions about vaccines? We'll call you.
                    </h3>
                    <p className="text-xs text-white/60 mt-1.5" style={{ fontFamily: 'Montserrat' }}>
                      No commitment. A certified coordinator will answer every question — in under 15 minutes.
                    </p>
                  </div>
                </div>

                <form onSubmit={submit} className="space-y-3">
                  {/* Honeypot — hidden from humans, dropped silently by server */}
                  <input
                    type="text"
                    name="company_website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  />
                  <input
                    data-testid="exit-intent-name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 text-sm focus:border-[#f37721]/50 focus:bg-white/15 outline-none transition-all"
                    style={{ fontFamily: 'Montserrat' }}
                  />
                  <input
                    data-testid="exit-intent-phone"
                    value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10}
                    required
                    type="tel"
                    placeholder="Phone number"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 text-sm focus:border-[#f37721]/50 focus:bg-white/15 outline-none transition-all"
                    style={{ fontFamily: 'Montserrat' }}
                  />
                  <button
                    data-testid="exit-intent-submit"
                    type="submit" disabled={loading}
                    onMouseDown={() => trackButtonClick('Exit Intent Callback', { source: 'exit_intent', phase: 'click' })}
                    className="w-full bg-[#f37721] hover:bg-[#d9651a] text-white rounded-full px-6 py-3 text-sm font-semibold transition-all hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading ? 'Submitting...' : <>Request My Callback <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <p data-testid="exit-intent-consent-microcopy"
                    className="text-[10.5px] leading-relaxed text-white/55 text-center pt-2"
                    style={{ fontFamily: 'Montserrat' }}>
                    By submitting, you agree that BookMyJab may contact you by phone, WhatsApp, SMS or email regarding your enquiry. See our{' '}
                    <Link to="/privacy-policy" className="underline hover:text-[#f37721] transition-colors">Privacy Policy</Link>.
                  </p>
                </form>

                <button
                  onClick={() => close('no-thanks')}
                  data-testid="exit-intent-decline"
                  className="mt-4 w-full text-center text-[11px] text-white/40 hover:text-white/70 transition-colors"
                  style={{ fontFamily: 'Montserrat' }}
                >
                  No thanks, I'll keep browsing
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
